import type { AlertDto, AlertStatus } from '@vision360/contracts';
import { Check, CircleCheck, CircleDot, MoonStar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const STATUS_STYLE: Record<AlertStatus, { icon: typeof Check; className: string }> = {
  open: { icon: CircleDot, className: 'bg-primary/12 text-primary' },
  acknowledged: { icon: Check, className: 'bg-muted text-foreground' },
  snoozed: { icon: MoonStar, className: 'bg-muted text-muted-foreground' },
  resolved: { icon: CircleCheck, className: 'bg-transparent text-muted-foreground' },
};

const clock = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** The alert's state; a snooze shows when it ends. */
export function StatusChip({ alert }: { alert: AlertDto }) {
  const { t } = useTranslation('alerts');
  const { icon: Icon, className } = STATUS_STYLE[alert.status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        className,
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {alert.status === 'snoozed' && alert.snoozedUntil
        ? t('status.snoozedUntil', { time: clock.format(new Date(alert.snoozedUntil)) })
        : t(`status.${alert.status}`)}
    </span>
  );
}
